import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ConnectionProfileCreateSchema,
  ConnectionProfileUpdateSchema,
  type ConnectionProfileCreate,
  type ConnectionProfileResponse,
  type ConnectionProfileUpdate,
} from '@flixflow/shared';
import { cn } from '../lib/cn.js';

interface Props {
  initial?: ConnectionProfileResponse;
  onSubmit: (data: ConnectionProfileCreate | ConnectionProfileUpdate) => Promise<void>;
  onCancel: () => void;
}

type FormValues = {
  name: string;
  host: string;
  port: number;
  database: string;
  username: string;
  password: string;
  encrypt: boolean;
  trustServerCert: boolean;
};

export function ConnectionProfileForm({ initial, onSubmit, onCancel }: Props) {
  const isEdit = !!initial;
  const schema = isEdit ? ConnectionProfileUpdateSchema : ConnectionProfileCreateSchema;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: initial
      ? {
          name: initial.name,
          host: initial.host,
          port: initial.port,
          database: initial.database,
          username: initial.username,
          password: '',
          encrypt: initial.encrypt,
          trustServerCert: initial.trustServerCert,
        }
      : { port: 1433, encrypt: true, trustServerCert: false },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Field label="Name" error={errors.name?.message}>
        <input {...register('name')} className={inputCls(!!errors.name)} placeholder="z.B. Produktion ERP" />
      </Field>

      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <Field label="Host / IP" error={errors.host?.message}>
            <input {...register('host')} className={inputCls(!!errors.host)} placeholder="db.example.com" />
          </Field>
        </div>
        <Field label="Port" error={errors.port?.message}>
          <input
            {...register('port', { valueAsNumber: true })}
            type="number"
            className={inputCls(!!errors.port)}
          />
        </Field>
      </div>

      <Field label="Datenbank" error={errors.database?.message}>
        <input {...register('database')} className={inputCls(!!errors.database)} placeholder="ERP_DB" />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Benutzername" error={errors.username?.message}>
          <input {...register('username')} className={inputCls(!!errors.username)} autoComplete="username" />
        </Field>
        <Field
          label={isEdit ? 'Passwort (leer = unverändert)' : 'Passwort'}
          error={errors.password?.message}
        >
          <input
            {...register('password')}
            type="password"
            className={inputCls(!!errors.password)}
            autoComplete="new-password"
          />
        </Field>
      </div>

      <div className="flex gap-6">
        <label className="flex items-center gap-2 text-sm">
          <input {...register('encrypt')} type="checkbox" className="rounded" />
          Verbindung verschlüsseln
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input {...register('trustServerCert')} type="checkbox" className="rounded" />
          Zertifikat vertrauen
        </label>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className={btnCls('secondary')}>
          Abbrechen
        </button>
        <button type="submit" disabled={isSubmitting} className={btnCls('primary')}>
          {isSubmitting ? 'Speichern …' : isEdit ? 'Speichern' : 'Erstellen'}
        </button>
      </div>
    </form>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-medium text-neutral-700">{label}</label>
      {children}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

function inputCls(hasError: boolean) {
  return cn(
    'w-full rounded border px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500',
    hasError ? 'border-red-400' : 'border-neutral-300',
  );
}

function btnCls(variant: 'primary' | 'secondary') {
  const base = 'px-4 py-1.5 rounded text-sm font-medium disabled:opacity-50';
  return variant === 'primary'
    ? cn(base, 'bg-blue-600 text-white hover:bg-blue-700')
    : cn(base, 'border border-neutral-300 text-neutral-700 hover:bg-neutral-50');
}
